import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AssetCommentAnnotationDto,
  AssetDto,
  CommentDto,
  CreateAssetCommentBody,
  DisplayGuestDto,
  UpdateAssetCommentBody,
} from '@nusign/picflow-backend-types';
import { Observable, catchError, map, tap } from 'rxjs';
import { Rect } from '../classes/rect.class';
import { VideoTimeData } from '../gallery-shared/components/video-viewer/video-viewer.component';
import { AnnotationHexToColor } from '../gallery-shared/types/annotation-color';
import { Shape } from '../gallery-shared/types/shapes';
import { GalleryAccessLevel } from '../lib/access-level';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { AppConfigService } from '../providers/config.service';
import { GalleryPasswordService } from './gallery-password.service';
import { GetAllService } from './get-all.service';
import { GuestAuthService } from './guest-auth.service';
import { GuestService } from './guest.service';
import { MergeAccountService } from './merge-account.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { UpdateService } from './update.service';

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private getAllService = inject(GetAllService);
  private observableLoader = inject(ObservableDataLoaderService);
  private updateService = inject(UpdateService);
  private state = inject(StateService);
  private guestService = inject(GuestService);
  private guestAuthService = inject(GuestAuthService);
  private mergeAccountService = inject(MergeAccountService);
  private analytics = inject(PICFLOW_ANALYTICS);
  guestSub = this.guestService.onUpdated.subscribe(updated => {
    if (!updated) return;
    this.observableLoader.forceDataRefreshFuzzy(`comments_`);
  });

  guestAuthSub = this.guestAuthService.onAuthChange.subscribe(auth => {
    if (!auth) return;
    setTimeout(() => {
      // giving time for other requests to fire, for example, post comments
      this.observableLoader.forceDataRefreshFuzzy(`comments_`);
    }, 100);
  });

  mergeSub = this.mergeAccountService.onMerged.subscribe(merged => {
    if (!merged) return;
    this.observableLoader.forceDataRefreshFuzzy(`comments_`);
  });

  private updateSub = this.updateService.onCommentUpdate.subscribe(async event => {
    if (!event) return;
    if (!event.comment?.text && !event.comment?.annotation) return;

    if (event.name === 'comment.created') {
      this.fixAnnotation(event.comment);
      this.observableLoader.update<CommentDto[]>(`comments_${event.comment.asset}`, comments => {
        if (!comments) return;
        if (!comments.some(c => c['id'] === event.comment['id'])) {
          comments.push(event.comment);
        }
        return comments;
      });
    }
    if (event.name === 'comment.updated') {
      this.observableLoader.update<CommentDto[]>(`comments_${event.comment.asset}`, comments => {
        const index = comments.findIndex(c => c['id'] === event.comment['id']);
        if (index !== -1) {
          comments[index] = event.comment;
          this.fixAnnotation(comments[index]);
        }
        return comments;
      });
    }
    if (event.name === 'comment.deleted') {
      this.observableLoader.update<CommentDto[]>(`comments_${event.comment.asset}`, comments => {
        const index = comments.findIndex(c => c['id'] === event.comment['id']);
        if (index !== -1) comments.splice(index, 1);
        return comments;
      });
    }
  });

  public getComments(assetId: string, galleryId: string): Observable<CommentDto[]> {
    return this.observableLoader.get(`comments_${assetId}`, () =>
      this.getAllService
        .getAll<CommentDto>(
          `${this.config.apiEndpoint}/assets/${assetId}/comments?sort[]=-created_at${this.getPassword(galleryId, true)}`
        )
        .pipe(
          catchError(() => []),
          map(comments => comments.filter(comment => comment.text || comment.annotation)),
          tap(comments => comments.forEach(this.fixAnnotation.bind(this)))
        )
    );
  }

  public filterCommentsForAccessLevel(
    comments: CommentDto[],
    accessLevel: GalleryAccessLevel,
    isGalleryGuest: boolean
  ) {
    return comments.filter(comment => {
      if (!comment.private) return true;
      return accessLevel === 'edit' && !isGalleryGuest;
    });
  }

  public postComment(
    assetId: string,
    galleryId: string,
    params: Partial<CreateAssetCommentBody>
  ): Observable<CommentDto> {
    return this.http
      .post<CommentDto>(`${this.config.apiEndpoint}/assets/${assetId}/comments${this.getPassword(galleryId)}`, params)
      .pipe(
        tap(comment => {
          this.observableLoader.update<CommentDto[]>(`comments_${assetId}`, comments => {
            comment.user = this.state.user;
            comment.guest = this.state.guest as unknown as DisplayGuestDto;
            if (!comments.some(c => c['id'] === comment['id'])) {
              this.fixAnnotation(comment);
              comments.push(comment);
            }
            return comments;
          });
          if (params.annotation) {
            this.analytics.track('annotation_created', false, this.getAnnotationAnalyticsData(params.annotation));
          }
        })
      );
  }

  public updateComment(
    assetId: string,
    commentId: string,
    params: Partial<UpdateAssetCommentBody>,
    galleryId: string
  ): Observable<CommentDto> {
    if (params.text === null) delete params.text;
    return this.http
      .patch<CommentDto>(
        `${this.config.apiEndpoint}/assets/${assetId}/comments/${commentId}${this.getPassword(galleryId)}`,
        params
      )
      .pipe(
        tap(comment => {
          this.observableLoader.update<CommentDto[]>(`comments_${assetId}`, comments => {
            const index = comments.findIndex(c => c['id'] === comment['id']);
            if (index === -1) return comments;
            const { user, guest } = comments[index];
            this.fixAnnotation(comment);
            comments[index] = comment;
            comments[index].user = user;
            comments[index].guest = guest;
            return comments;
          });
        })
      );
  }

  public deleteComment(assetId: string, commentId: string, galleryId: string) {
    return this.http
      .delete(`${this.config.apiEndpoint}/assets/${assetId}/comments/${commentId}${this.getPassword(galleryId)}`)
      .pipe(
        tap(() => {
          this.observableLoader.update<CommentDto[]>(`comments_${assetId}`, comments => {
            const index = comments.findIndex(c => c['id'] === commentId);
            if (index === -1) return comments;
            comments.splice(index, 1);
            return comments;
          });
        })
      );
  }

  private getAnnotationAnalyticsData(data: Shape[]) {
    const colors = new Set<string>();
    const tools = new Set<string>();

    data.forEach(shape => {
      colors.add(shape.color);
      tools.add(shape.tool);
    });

    const colorObj = {};
    colors.forEach(color => (colorObj[color] = true));

    const toolObj = {};
    tools.forEach(tool => (toolObj[tool] = true));

    return {
      colors: colorObj,
      tools: toolObj,
    };
  }

  public fixAnnotation(comment: CommentDto) {
    if (!comment.annotation) return;
    (comment.annotation as AssetCommentAnnotationDto[]).forEach(shape => {
      shape.color = AnnotationHexToColor(shape.color);

      if (shape.tool === 'rect' && shape.radius === 0) {
        // old circle, not sure if still needed
        shape.tool = <'rect'>'oval';
      }

      if (shape['tool'] === 'oval') {
        shape['x1'] = shape.x;
        shape['y1'] = shape.y;
        shape['x2'] = shape.w;
        shape['y2'] = shape.h;
      }
    });
  }

  public getCommentPosition(asset: AssetDto, videoTime: VideoTimeData, pos?: Rect) {
    const x = (pos?.width ? ([pos.x, pos.x + pos.width] as [number, number]) : pos?.x) ?? null;
    const y = (pos?.height ? ([pos.y, pos.y + pos.height] as [number, number]) : pos?.y) ?? null;

    if (asset.type === 'video') {
      const frame = this.getCommentFramePosition(asset, videoTime);
      if (frame !== undefined) {
        return {
          frame,
          x,
          y,
        };
      }
    }

    if (pos) {
      return {
        x,
        y,
        frame: null,
      };
    }
  }

  private getCommentFramePosition(asset: AssetDto, videoTime: VideoTimeData) {
    if (asset.type !== 'video') return;
    if (!videoTime) return;
    return videoTime.currentFrame / videoTime.durationFrames;
  }

  private getPassword(galleryId: string, and = false) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    if (and) return '&password=' + encodeURIComponent(password);
    return '?password=' + encodeURIComponent(password);
  }
}
